import type { RestaurantResponse } from '@/features/restaurants/types';
import type { TFunction } from 'i18next';

const DAY_KEYS: Record<string, string> = {
  SUNDAY: 'sunday',
  MONDAY: 'monday',
  TUESDAY: 'tuesday',
  WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday',
  FRIDAY: 'friday',
  SATURDAY: 'saturday',
};
const DAY_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

type HoursInput =
  | RestaurantResponse['openingHours']
  | RestaurantResponse['deliveryHours']
  | Record<string, Array<{ open: string; close: string }>>
  | undefined;

function getTodayHoursString(hours: HoursInput): string | null {
  if (!hours) return null;

  let hoursArray: Array<{ day: string; isClosed?: boolean; ranges: Array<{ openTime?: string; closeTime?: string; open?: string; close?: string }> }>;
  if (Array.isArray(hours)) {
    hoursArray = hours;
  } else if (typeof hours === 'object' && hours !== null) {
    hoursArray = Object.entries(hours).map(([day, ranges]) => ({
      day: day.toUpperCase(),
      ranges: Array.isArray(ranges)
        ? ranges.map((r: { open?: string; close?: string }) => ({ openTime: r.open, closeTime: r.close }))
        : [],
      isClosed: !ranges || (Array.isArray(ranges) && ranges.length === 0),
    }));
  } else {
    return null;
  }

  if (hoursArray.length === 0) return null;

  const today = new Date().getDay();
  const todayKey = DAY_ORDER[today];
  const todayHours = hoursArray.find((h) => h?.day && DAY_KEYS[h.day] === todayKey);

  if (todayHours && !todayHours.isClosed && todayHours.ranges?.length) {
    const first = todayHours.ranges[0];
    const openTime = first?.openTime ?? first?.open;
    const closeTime = first?.closeTime ?? first?.close;
    if (openTime && closeTime) return `${openTime}–${closeTime}`;
  }
  return null;
}

/** Public wrapper to get today's hours string from openingHours or deliveryHours */
export function getTodayHours(hours: HoursInput): string | null {
  return getTodayHoursString(hours);
}

/**
 * Returns the open/close time ranges for a given JS day-of-week index (0=Sun … 6=Sat).
 * Handles both DayHours[] and Record<string,{open,close}[]> backend formats.
 * Returns an empty array when the restaurant is closed that day or hours are unknown.
 */
export function getHoursRangesForDay(
  hours: HoursInput,
  dayIndex: number,
): Array<{ openTime: string; closeTime: string }> {
  if (!hours) return [];

  const dayName = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][dayIndex];
  if (!dayName) return [];

  if (Array.isArray(hours)) {
    const entry = hours.find((h) => h?.day === dayName);
    if (!entry || entry.isClosed || !entry.ranges?.length) return [];
    return entry.ranges
      .map((r) => ({ openTime: (r as { openTime?: string; open?: string }).openTime ?? (r as { open?: string }).open ?? '', closeTime: (r as { closeTime?: string; close?: string }).closeTime ?? (r as { close?: string }).close ?? '' }))
      .filter((r) => r.openTime && r.closeTime);
  }

  if (typeof hours === 'object') {
    const dayKey = dayName.toLowerCase();
    const entry = (hours as Record<string, Array<{ open: string; close: string }>>)[dayKey];
    if (!entry?.length) return [];
    return entry.map((r) => ({ openTime: r.open, closeTime: r.close })).filter((r) => r.openTime && r.closeTime);
  }

  return [];
}

export type StoreStatusResult = {
  type: 'success' | 'warning' | 'closed';
  message: string;
} | null;

/**
 * Returns store status for the navbar.
 * Uses comprehensive messages for all order/delivery scenarios.
 * @param compact - When true, returns a shorter format for mobile
 */
export function getStoreStatusForNavbar(
  restaurant: RestaurantResponse | null | undefined,
  t: TFunction,
  options?: { compact?: boolean }
): StoreStatusResult {
  if (!restaurant) return null;

  const compact = options?.compact ?? false;

  if (restaurant.isTemporarilyClosed || restaurant.closedReason || restaurant.closeMessage) {
    return {
      type: 'warning',
      message: restaurant.closedReason || restaurant.closeMessage || t('landing.temporarilyClosed'),
    };
  }

  if (restaurant.isClosed) {
    return {
      type: 'closed',
      message: restaurant.closeMessage || t('public.closed', 'Closed'),
    };
  }

  if (!restaurant.isStoreOpen) {
    return {
      type: 'closed',
      message: restaurant.closeMessage || t('landing.currentlyClosed'),
    };
  }

  // Store is open – show timings ONLY when that service is open
  const storeHours = getTodayHoursString(restaurant.openingHours);
  // Delivery hours: only when delivery is actually open
  const deliveryHours =
    restaurant.hasDelivery && restaurant.isDeliveryOpen
      ? getTodayHoursString(restaurant.deliveryHours)
      : null;
  const deliveryClosed = restaurant.hasDelivery && !restaurant.isDeliveryOpen;
  const deliveryDisabled = !restaurant.hasDelivery;

  if (compact) {
    const parts: string[] = [];
    // Store: show hours only when we have them (store is open)
    if (storeHours) parts.push(storeHours);
    else parts.push(t('landing.navOpen', 'Open'));
    // Delivery: show hours ONLY when open; when closed show message only (no timings)
    if (deliveryHours) parts.push(`${t('landing.delivery')} ${deliveryHours}`);
    else if (deliveryClosed) parts.push(t('landing.deliveryClosed'));
    else if (deliveryDisabled)
      parts.push(
        restaurant.hasPickup
          ? storeHours
            ? `${t('landing.navPickupOnly', 'Pickup')} ${storeHours}`
            : t('landing.navPickupOnly', 'Pickup')
          : t('landing.noDelivery')
      );
    return { type: 'success', message: parts.join(' · ') };
  }

  const parts: string[] = [];
  if (storeHours) parts.push(`${t('landing.navOpen', 'Open')} • ${storeHours}`);
  else parts.push(t('landing.navOpen', 'Open'));
  // Delivery: timings only when open; when closed just the message
  if (deliveryHours) parts.push(`${t('landing.delivery')} • ${deliveryHours}`);
  else if (deliveryClosed) parts.push(t('landing.deliveryClosed'));
  else if (deliveryDisabled)
    parts.push(
      restaurant.hasPickup
        ? storeHours
          ? `${t('landing.navPickupOnly', 'Pickup only')} • ${storeHours}`
          : t('landing.navPickupOnly', 'Pickup only')
        : t('landing.noDelivery')
    );

  return { type: 'success', message: parts.join(' | ') };
}

/** Parse "HH:mm" to minutes since midnight */
function parseTimeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/** Check if a given time (minutes since midnight) is within a range [open, close]. Close is inclusive. */
function isTimeInRange(
  nowMinutes: number,
  openStr: string | undefined,
  closeStr: string | undefined
): boolean {
  if (!openStr || !closeStr) return false;
  const open = parseTimeToMinutes(openStr);
  const close = parseTimeToMinutes(closeStr);
  if (open <= close) return nowMinutes >= open && nowMinutes <= close;
  return nowMinutes >= open || nowMinutes <= close;
}

/** Check if current time is within today's hours for the given hours config */
function isWithinTodayHours(hours: HoursInput, now: Date = new Date()): boolean {
  if (!hours) return false;

  let hoursArray: Array<{ day: string; isClosed?: boolean; ranges: Array<{ openTime?: string; closeTime?: string; open?: string; close?: string }> }>;
  if (Array.isArray(hours)) {
    hoursArray = hours;
  } else if (typeof hours === 'object' && hours !== null) {
    hoursArray = Object.entries(hours).map(([day, ranges]) => ({
      day: day.toUpperCase(),
      ranges: Array.isArray(ranges)
        ? ranges.map((r: { open?: string; close?: string }) => ({ openTime: r.open, closeTime: r.close }))
        : [],
      isClosed: !ranges || (Array.isArray(ranges) && ranges.length === 0),
    }));
  } else {
    return false;
  }

  const today = now.getDay();
  const todayKey = DAY_ORDER[today];
  const todayHours = hoursArray.find((h) => h?.day && DAY_KEYS[h.day] === todayKey);
  if (!todayHours || todayHours.isClosed || !todayHours.ranges?.length) return false;

  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  return todayHours.ranges.some((r) => {
    const open = r.openTime ?? r.open;
    const close = r.closeTime ?? r.close;
    return isTimeInRange(nowMinutes, open, close);
  });
}

/**
 * Returns true if within hours, false if outside hours, null if no hours data (unknown).
 * When null, caller should allow order if backend says store/delivery is open.
 */
function isWithinTodayHoursOrUnknown(hours: HoursInput, now: Date = new Date()): boolean | null {
  if (!hours) return null;
  let hoursArray: Array<{ day: string; isClosed?: boolean; ranges: Array<{ openTime?: string; closeTime?: string; open?: string; close?: string }> }>;
  if (Array.isArray(hours)) {
    hoursArray = hours;
  } else if (typeof hours === 'object' && hours !== null) {
    hoursArray = Object.entries(hours).map(([day, ranges]) => ({
      day: day.toUpperCase(),
      ranges: Array.isArray(ranges)
        ? ranges.map((r: { open?: string; close?: string }) => ({ openTime: r.open, closeTime: r.close }))
        : [],
      isClosed: !ranges || (Array.isArray(ranges) && ranges.length === 0),
    }));
  } else {
    return null;
  }
  if (hoursArray.length === 0) return null;
  const today = now.getDay();
  const todayKey = DAY_ORDER[today];
  const todayHours = hoursArray.find((h) => h?.day && DAY_KEYS[h.day] === todayKey);
  if (!todayHours || todayHours.isClosed || !todayHours.ranges?.length) return null;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const within = todayHours.ranges.some((r) => {
    const open = r.openTime ?? r.open;
    const close = r.closeTime ?? r.close;
    return isTimeInRange(nowMinutes, open, close);
  });
  return within;
}

export type OrderHoursCheck = { allowed: boolean; message?: string };

/**
 * Check if an order can be placed now based on order type and restaurant hours.
 * - DELIVERY: must be within deliveryHours
 * - PICKUP / DINE_IN: must be within openingHours
 */
export function canPlaceOrder(
  restaurant: RestaurantResponse | null | undefined,
  orderType: 'delivery' | 'pickup' | 'dine_in',
  t?: TFunction
): OrderHoursCheck {
  if (!restaurant) return { allowed: false, message: t?.('landing.failedToLoadRestaurant') };
  if (restaurant.isClosed || restaurant.isTemporarilyClosed) {
    return { allowed: false, message: restaurant.closeMessage || restaurant.closedReason || t?.('landing.temporarilyClosed') };
  }

  if (orderType === 'delivery') {
    if (!restaurant.hasDelivery) return { allowed: false, message: t?.('landing.noDelivery') };
    if (restaurant.isDeliveryOpen === false) {
      return { allowed: false, message: t?.('landing.deliveryClosed') || 'Delivery currently unavailable' };
    }
  } else {
    if (restaurant.isStoreOpen === false) {
      return { allowed: false, message: t?.('landing.currentlyClosed') || 'Currently closed' };
    }
  }

  const now = new Date();

  if (orderType === 'delivery') {
    const deliveryHoursCheck = isWithinTodayHoursOrUnknown(restaurant.deliveryHours, now);
    if (deliveryHoursCheck === false) {
      return { allowed: false, message: t?.('landing.deliveryClosed') || 'Delivery currently unavailable' };
    }
    if (deliveryHoursCheck === null && restaurant.isDeliveryOpen !== true) {
      return { allowed: false, message: t?.('landing.deliveryClosed') || 'Delivery currently unavailable' };
    }
  } else if (orderType === 'pickup') {
    // pickupTimeRanges holds estimated wait-time windows, not opening hours — never use it to block orders
    const storeHoursCheck = isWithinTodayHoursOrUnknown(restaurant.openingHours, now);
    if (storeHoursCheck === false) {
      return { allowed: false, message: t?.('landing.currentlyClosed') || 'Currently closed' };
    }
    if (storeHoursCheck === null && restaurant.isStoreOpen !== true) {
      return { allowed: false, message: t?.('landing.currentlyClosed') || 'Currently closed' };
    }
  } else {
    const storeHoursCheck = isWithinTodayHoursOrUnknown(restaurant.openingHours, now);
    if (storeHoursCheck === false) {
      return { allowed: false, message: t?.('landing.currentlyClosed') || 'Currently closed' };
    }
    if (storeHoursCheck === null && restaurant.isStoreOpen !== true) {
      return { allowed: false, message: t?.('landing.currentlyClosed') || 'Currently closed' };
    }
  }

  return { allowed: true };
}
